/**
 * Curated presets. `ivory` is the reference look: a warm cream page with very
 * light, tightly-tracked didone caps. Every other preset keeps that structure
 * and only swaps the palette + type pairing, so switching presets never breaks
 * a layout the creator has already tuned.
 *
 * Fonts are limited to this list because the public page loads them from
 * Google Fonts by family name — see `GOOGLE_FONTS` below.
 */
export const THEME_PRESETS = [
    {
        id: 'ivory',
        name: 'Ivory',
        description: 'Warm cream and soft brown. Light didone caps set very large.',
        tokens: {
            bg: '#FFF6DE',
            surface: '#EFE4C8',
            text: '#59493B',
            muted: '#8A7862',
            accent: '#59493B',
            onAccent: '#FFF6DE',
            displayFont: 'Cormorant Garamond',
            bodyFont: 'Jost',
            displayWeight: 300,
            displayTracking: '-0.025em',
            displayTransform: 'uppercase',
            radius: '2px',
        },
    },
    {
        id: 'noir',
        name: 'Noir',
        description: 'Near-black page with ivory type. Media reads like a gallery wall.',
        tokens: {
            bg: '#0F1015',
            surface: '#1C1D24',
            text: '#F3EEE4',
            muted: '#9B9689',
            accent: '#E8DFC9',
            onAccent: '#0F1015',
            displayFont: 'Playfair Display',
            bodyFont: 'Jost',
            displayWeight: 400,
            displayTracking: '-0.02em',
            displayTransform: 'uppercase',
            radius: '2px',
        },
    },
    {
        id: 'blush',
        name: 'Blush',
        description: 'Soft pink and deep plum. Warmer and more editorial.',
        tokens: {
            bg: '#F9EAE6',
            surface: '#EFD8D2',
            text: '#4A2F3C',
            muted: '#8C6B76',
            accent: '#B0687A',
            onAccent: '#FFFFFF',
            displayFont: 'Fraunces',
            bodyFont: 'Jost',
            displayWeight: 300,
            displayTracking: '-0.02em',
            displayTransform: 'uppercase',
            radius: '6px',
        },
    },
    {
        id: 'sage',
        name: 'Sage',
        description: 'Muted green and forest ink. Calm, wellness-leaning.',
        tokens: {
            bg: '#E9EDE4',
            surface: '#D7DECF',
            text: '#33402F',
            muted: '#6C7A66',
            accent: '#4E6147',
            onAccent: '#F2F5EE',
            displayFont: 'Marcellus',
            bodyFont: 'Jost',
            displayWeight: 400,
            displayTracking: '-0.015em',
            displayTransform: 'uppercase',
            radius: '2px',
        },
    },
    {
        id: 'editorial',
        name: 'Editorial',
        description: 'Stark white and black. Maximum contrast, magazine styling.',
        tokens: {
            bg: '#FFFFFF',
            surface: '#F0F0F0',
            text: '#111111',
            muted: '#6B6B6B',
            accent: '#111111',
            onAccent: '#FFFFFF',
            displayFont: 'Bodoni Moda',
            bodyFont: 'Inter',
            displayWeight: 400,
            displayTracking: '-0.03em',
            displayTransform: 'uppercase',
            radius: '0px',
        },
    },
    {
        id: 'sunset',
        name: 'Sunset',
        description: 'Terracotta and clay. Bolder, travel and food friendly.',
        tokens: {
            bg: '#FBEFE4',
            surface: '#F1DCC8',
            text: '#5B3327',
            muted: '#96695A',
            accent: '#C4643C',
            onAccent: '#FFF6EE',
            displayFont: 'Italiana',
            bodyFont: 'Jost',
            displayWeight: 400,
            displayTracking: '-0.01em',
            displayTransform: 'uppercase',
            radius: '4px',
        },
    },
];
/** Display faces offered in the editor's font picker. */
export const DISPLAY_FONTS = [
    'Cormorant Garamond', 'Playfair Display', 'Fraunces', 'Marcellus',
    'Bodoni Moda', 'Italiana', 'Libre Baskerville', 'Syne',
];
/** Body faces offered in the editor's font picker. */
export const BODY_FONTS = ['Jost', 'Inter', 'Karla', 'Work Sans', 'DM Sans'];
/** Every family the public page may need to request from Google Fonts. */
export const GOOGLE_FONTS = [...DISPLAY_FONTS, ...BODY_FONTS];
export function getPreset(id) {
    return THEME_PRESETS.find((p) => p.id === id);
}
export const DEFAULT_THEME_SETTINGS = {
    presetId: 'ivory',
    tokens: { ...THEME_PRESETS[0].tokens },
    heroLayout: 'scatter',
    showDurations: true,
};
/** CSS custom properties for a token set — the single place tokens become CSS. */
export function themeCssVars(t) {
    return {
        '--ugc-bg': t.bg,
        '--ugc-surface': t.surface,
        '--ugc-text': t.text,
        '--ugc-muted': t.muted,
        '--ugc-accent': t.accent,
        '--ugc-on-accent': t.onAccent,
        '--ugc-display-font': `'${t.displayFont}', Georgia, serif`,
        '--ugc-body-font': `'${t.bodyFont}', system-ui, sans-serif`,
        '--ugc-display-weight': String(t.displayWeight),
        '--ugc-display-tracking': t.displayTracking,
        '--ugc-display-transform': t.displayTransform,
        '--ugc-radius': t.radius,
    };
}
/**
 * Relative luminance per WCAG 2.1. Used to keep creator-picked colours legible:
 * the editor warns below 4.5:1 for body text.
 */
function luminance(hex) {
    const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
    if (!m)
        return 0;
    const int = parseInt(m[1], 16);
    const channels = [(int >> 16) & 255, (int >> 8) & 255, int & 255].map((c) => {
        const s = c / 255;
        return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
    });
    return 0.2126 * channels[0] + 0.7152 * channels[1] + 0.0722 * channels[2];
}
/** WCAG contrast ratio between two hex colours (1–21). */
export function contrastRatio(a, b) {
    const la = luminance(a);
    const lb = luminance(b);
    const [hi, lo] = la > lb ? [la, lb] : [lb, la];
    return (hi + 0.05) / (lo + 0.05);
}
export const MIN_TEXT_CONTRAST = 4.5;
//# sourceMappingURL=themes.js.map